import React, { useState, useEffect, useMemo } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import { api } from "../../utils/axios-instance";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import { useDarkMode } from "../../context/darkModeContext";
import useAuth from "../../stores/authStore";
import { auth } from "../../helper/firebaseClient";

const Trash = `${process.env.PUBLIC_URL}/assets/trash.webp`;

export default function TeamManagementTable() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const { darkMode } = useDarkMode();
  const profileData = useAuth((state) => state.profileData);
  const currentUserId = profileData?.id || auth?.currentUser?.uid || null;
  const currentRole = profileData?.role_priv;

  const canManage =
    currentRole === "org_admin" || currentRole === "super_admin";

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [openDeptIndex, setOpenDeptIndex] = useState(null);

  // ─── Fetch all data on mount
  useEffect(() => {
    const listUsers = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/users/list_users`);
        const filtered = (res.data || []).filter(
          (u) => u.status === "onboarded" && u.flags?.is_approved === true
        );
        setUsers(filtered);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    listUsers();
  }, []);

  useEffect(() => {
    const getRoles = async () => {
      try {
        const role = await api.get(`/role/listRoles`);
        setRoles(role.data || []);
      } catch (error) {
        console.log("Error fetching roles", error);
      }
    };
    getRoles();
  }, []);

  // Fetch org departments for dept dropdown
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const dep = await api.get(`/dept/listDepts`);
        setDepartments(dep.data || []);
      } catch (error) {
        console.log("Error fetching departments", error);
        setDepartments([]);
      }
    };
    fetchDepartments();
  }, []);

  // ─── Column definitions
  const columns = useMemo(
    () => [
      { id: "name", label: "Team Member", minWidth: 170 },
      { id: "status", label: "Status", minWidth: 60 },
      { id: "dateAdded", label: "Date Added", minWidth: 100 },
      { id: "departments", label: "Departments", minWidth: 260 },
      { id: "access", label: "Access", minWidth: 140 },
      { id: "delete", label: "Delete", minWidth: 80 },
    ],
    []
  );

  const toDate = (value) => {
    if (!value) return null;

    if (typeof value === "object" && value.seconds) {
      return new Date(value.seconds * 1000);
    }

    if (value instanceof Date) return value;

    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (value) => {
    const d = toDate(value);
    if (!d) return "—";
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
    });
  };

  const deptMap = useMemo(() => {
    const m = new Map();
    (departments || []).forEach((d) => m.set(d.id, d.name));
    return m;
  }, [departments]);

  const buildDeptDisplay = (user) => {
    const userRole = user?.role_priv;

    // org_admin/super_admin users should DISPLAY all depts with count
    const isAdminUser = userRole === "org_admin" || userRole === "super_admin";

    // Admin/super_admin always shows ALL departments
    if (isAdminUser) {
      const names = (departments || []).map((d) => d?.name).filter(Boolean);
      return {
        names,
        count: names.length,
        // Select value still needs something; we won't allow changing anyway for admins row if self/blocked
        // value: user?.dept_ids?.[0] || "",
        value: "",
      };
    }

    // Employee: prefer dept_names array, then resolve from dept_ids, then legacy
    const namesFromProfile = (user?.dept_names || []).filter(Boolean);
    if (namesFromProfile.length) {
      return {
        names: namesFromProfile,
        count: namesFromProfile.length,
        value: user?.dept_ids?.[0] || "",
      };
    }

    const ids = user?.dept_ids || [];
    if (ids.length) {
      const names = ids.map((id) => deptMap.get(id)).filter(Boolean);
      return { names, count: names.length, value: ids[0] || "" };
    }

    // legacy single-dept fallback
    if (user?.dept && deptMap.get(user.dept)) {
      return { names: [deptMap.get(user.dept)], count: 1, value: user.dept };
    }

    return { names: [], count: 0, value: "" };
  };

  function createData(data) {
    const isActive = Boolean(data?.is_active ?? data?.flags?.is_active);
    const deptInfo = buildDeptDisplay(data);

    return {
      id: data.id,
      raw: data, // keep raw ref for updates
      name: `${data?.name || ""} ${data?.last_name || ""}`.trim(),
      status: (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 2,
            py: 0.5,
            borderRadius: "999px",
            fontSize: 14,
            fontWeight: 500,
            minWidth: 96,
            ...(isActive
              ? { color: "#027A48", backgroundColor: "#ECFDF3" }
              : { color: "#B42318", backgroundColor: "#FEF3F2" }),
          }}
        >
          <Box className={isActive ? "online-dot" : "offline-dot"} />
          {isActive ? "Online" : "Offline"}
        </Box>
      ),
      dateAdded: formatDate(data?.created_at ?? data?.audit?.createdAt),

      dept_names: deptInfo.names,
      dept_count: deptInfo.count,
      dept_value: deptInfo.value,

      profilePictureUrl: data.profilePictureUrl,
      email: data.email,
      access: data.role_priv,
      delete: <img src={Trash} alt="Delete" className="cursor-pointer" />,
    };
  }

  const rows = useMemo(
    () => users.map((u) => createData(u)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, departments, deptMap]
  );

  // ALL role + dept mutations on other users go through PATCH /users/elevate/{id}.
  const elevateUser = async (userId, payload) => {
    await api.patch(`/users/elevate/${userId}`, payload);
  };

  // Role change handler
  const handleAccessChange = async (event, index) => {
    const newRole = event.target.value;
    const user = users[index];

    if (!user?.id) return;

    if (user.id === currentUserId) {
      toast.error("You can't change your own role here.");
      return;
    }

    const isBecomingEmployee = newRole === "employee";
    const isBecomingAdmin =
      newRole === "org_admin" || newRole === "super_admin";

    // Build the PATCH payload
    const patch = { role_priv: newRole };

    if (isBecomingEmployee) {
      // Must send dept_ids for employee role — backend enforces minimum 1 dept.
      // Resolve best available set of dept ids for this user.
      const prevIds =
        Array.isArray(user._prev_depts) && user._prev_depts.length
          ? user._prev_depts
          : Array.isArray(user.dept_ids) && user.dept_ids.length
            ? user.dept_ids
            : departments.length
              ? [departments[0].id] // absolute fallback: first org dept
              : [];

      if (prevIds.length === 0) {
        toast.error("No departments available. Create one first.");
        return;
      }

      patch.dept_ids = prevIds;
      // Also send names so backend can cache them without an extra Firestore read
      patch.dept_names = prevIds.map((id) => deptMap.get(id)).filter(Boolean);
    }
    // For org_admin/super_admin: backend auto-assigns ALL depts — no dept_ids needed.

    // Optimistic UI update
    setUsers((prev) => {
      const updated = [...prev];
      const u = { ...updated[index] };
      u.role_priv = newRole;

      if (isBecomingAdmin) {
        u._prev_depts = Array.isArray(u.dept_ids) ? [...u.dept_ids] : [];
        u.dept_ids = departments.map((d) => d.id);
        u.dept_names = departments.map((d) => d.name);
        setOpenDeptIndex(null);
      } else if (isBecomingEmployee) {
        u.dept_ids = patch.dept_ids;
        u.dept_names = patch.dept_names;
        setOpenDeptIndex(index); // open dept dropdown so admin can refine
      }

      updated[index] = u;
      return updated;
    });

    try {
      await elevateUser(user.id, patch);
      toast.success("Role updated successfully");
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.error || "Failed to update role");
      // Roll back optimistic update
      setUsers((prev) => {
        const updated = [...prev];
        updated[index] = user;
        return updated;
      });
    }
  };

  // Department change handler
  const handleDeptChange = async (user, newDepartments) => {
    if (!user?.id) return;

    // permission checks
    if (!canManage) return;

    if (user.id === currentUserId) {
      toast.error("You can't change your own departments here.");
      return;
    }

    if (user.role_priv !== "employee") {
      toast.error("Departments for admins are set to all automatically.");
      return;
    }

    // Only employees can have custom dept assignments
    if (user.role_priv === "employee" && newDepartments.length === 0) {
      toast.error("Employee must have at least one department");
      return;
    }

    if (user.id === currentUserId) {
      toast.error("You can't change your own department here.");
      return;
    }

    const dept_ids = newDepartments.map((d) => d.id);
    const dept_names = newDepartments.map((d) => d.name);

    // optimistic UI update
    setUsers((prev) =>
      prev.map((u) => (u.id === user.id ? { ...u, dept_ids, dept_names } : u))
    );

    try {
      // elevate endpoint, NOT update-profile
      await elevateUser(user.id, { dept_ids, dept_names });
      toast.success("Departments updated");
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.error || "Failed to update departments"
      );
      // Roll back
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, dept_ids: user.dept_ids, dept_names: user.dept_names }
            : u
        )
      );
    }
  };

  // ─── Delete handlers
  const requestDeleteUser = (index) => {
    setPendingDeleteIndex(index);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setPendingDeleteIndex(null);
  };

  const confirmDeleteUser = async () => {
    if (pendingDeleteIndex === null) return;

    const deletedUserId = users[pendingDeleteIndex]?.id;
    if (!deletedUserId) return;

    setDeleting(true);
    try {
      await api.delete(`/users/deleteUser/${deletedUserId}`);

      toast.success("User deleted successfully");

      // REMOVE USER FROM UI
      setUsers((prev) => prev.filter((_, i) => i !== pendingDeleteIndex));

      setConfirmOpen(false);
      setPendingDeleteIndex(null);
    } catch (error) {
      console.log(error);
      toast.error("Error deleting user");
    } finally {
      setDeleting(false);
    }
  };

  const pendingUser =
    pendingDeleteIndex !== null ? users[pendingDeleteIndex] : null;

  return (
    <Box>
      <TableContainer
        sx={{
          maxHeight: 420,
          borderRadius: "12px",
          border: darkMode ? "1px solid #334155" : "1px solid #EAECF0",
          backgroundColor: darkMode ? "#1E293B" : "white",
        }}
      >
        <Table
          stickyHeader
          aria-label="team table"
          sx={{
            tableLayout: "auto",
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    minWidth: column.width,
                    backgroundColor: darkMode ? "#0F172A" : "#FCFCFD",
                    fontWeight: 700,
                    color: darkMode ? "#FFFFFF" : "#101828",
                    borderBottom: darkMode
                      ? "1px solid #334155"
                      : "1px solid #EAECF0",
                    py: 1.5,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow
                  key={i}
                  sx={{ backgroundColor: darkMode ? "#0F172A" : "#fff" }}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={col.id}
                      sx={{
                        borderBottom: darkMode
                          ? "1px solid #334155"
                          : "1px solid #EAECF0",
                      }}
                    >
                      <Skeleton
                        variant="text"
                        height={30}
                        sx={{ bgcolor: darkMode ? "#1E293B" : undefined }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 6,
                      color: "#667085",
                      fontSize: 14,
                    }}
                  >
                    No team members yet. Ask them to onboard or check the lobby
                    to approve pending users.
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const userId = row.id;
                const isSelf =
                  userId && currentUserId && userId === currentUserId;

                const userIsEmployee = users[index]?.role_priv === "employee";

                // Dept dropdown only editable for employees, not self, with canManage
                const deptSelectDisabled =
                  !canManage || isSelf || !userIsEmployee;

                // Role select disabled if self
                const roleSelectDisabled = !canManage || isSelf;

                const deptText =
                  row.dept_count > 0
                    ? `${row.dept_names.join(", ")} (${row.dept_count})`
                    : "—";

                return (
                  <TableRow
                    key={row.id || index}
                    hover
                    sx={{
                      backgroundColor: darkMode ? "#0F172A" : "#fff",
                      "&:hover": {
                        backgroundColor: darkMode ? "#1E293B" : "#F9FAFB",
                      },
                      "& td": {
                        borderBottom: darkMode
                          ? "1px solid #334155"
                          : "1px solid #EAECF0",
                        color: darkMode ? "#E2E8F0" : "#101828",
                      },
                    }}
                  >
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sx={{
                          width: column.width,
                          py: 1.25,
                          verticalAlign: "middle",
                        }}
                      >
                        {column.id === "name" ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                            }}
                          >
                            <Tooltip title={row.email || ""} arrow>
                              <Avatar
                                alt={row.name}
                                src={row.profilePictureUrl}
                                variant="rounded"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "10px",
                                }}
                              />
                            </Tooltip>
                            <Box
                              sx={{
                                fontWeight: 600,
                                color: darkMode ? "#FFFFFF" : "#101828",
                              }}
                            >
                              {row.name || "—"}
                            </Box>
                          </Box>
                        ) : column.id === "departments" ? (
                          <Tooltip
                            title={deptSelectDisabled ? deptText : ""}
                            arrow
                          >
                            {/* span wrapper: Tooltip needs a non-disabled child */}
                            <span style={{ display: "block", maxWidth: 260 }}>
                              <Autocomplete
                                multiple
                                options={departments}
                                getOptionLabel={(option) => option.name}
                                value={departments.filter((d) =>
                                  (users[index]?.dept_ids || []).includes(d.id)
                                )}
                                open={openDeptIndex === index}
                                onOpen={() => {
                                  if (!deptSelectDisabled)
                                    setOpenDeptIndex(index);
                                }}
                                onClose={() => setOpenDeptIndex(null)}
                                disableCloseOnSelect
                                limitTags={1}
                                onChange={(event, newValue) =>
                                  handleDeptChange(users[index], newValue)
                                }
                                renderTags={(value) => {
                                  if (!value.length) return null;
                                  return (
                                    <Box sx={{ fontSize: 13, fontWeight: 500 }}>
                                      {value[0].name}
                                      {value.length > 1 &&
                                        ` +${value.length - 1}`}
                                    </Box>
                                  );
                                }}
                                renderOption={(props, option, { selected }) => (
                                  <li {...props}>
                                    <Checkbox
                                      checked={selected}
                                      sx={{ mr: 1 }}
                                    />
                                    {option.name}
                                  </li>
                                )}
                                renderInput={(params) => {
                                  const selectedIds =
                                    users[index]?.dept_ids || [];
                                  const selectedCount = selectedIds.length;

                                  let displayText = "";
                                  if (!userIsEmployee) {
                                    displayText = `All (${departments.length})`;
                                  } else if (selectedCount === 1) {
                                    const dept = departments.find(
                                      (d) => d.id === selectedIds[0]
                                    );
                                    displayText = dept?.name || "";
                                  } else if (selectedCount > 1) {
                                    const firstDept = departments.find(
                                      (d) => d.id === selectedIds[0]
                                    );
                                    displayText = `${firstDept?.name || ""} +${selectedCount - 1}`;
                                  }

                                  return (
                                    <TextField
                                      {...params}
                                      value={displayText}
                                      size="small"
                                      inputProps={{
                                        ...params.inputProps,
                                        readOnly: true,
                                      }}
                                      sx={{
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "10px",
                                          height: 40,
                                          fontSize: 13,
                                          color: darkMode
                                            ? "#E2E8F0"
                                            : "#101828",
                                          backgroundColor: deptSelectDisabled
                                            ? darkMode
                                              ? "#1E293B"
                                              : "#F9FAFB"
                                            : darkMode
                                              ? "#1E293B"
                                              : "#fff",
                                          "& .MuiOutlinedInput-notchedOutline":
                                            {
                                              borderColor: darkMode
                                                ? "#334155"
                                                : undefined,
                                            },
                                        },
                                        "& input::placeholder": {
                                          display: "none",
                                        },
                                        "& input": {
                                          cursor: deptSelectDisabled
                                            ? "default"
                                            : "pointer",
                                          color: darkMode
                                            ? "#E2E8F0"
                                            : "#101828",
                                        },
                                      }}
                                    />
                                  );
                                }}
                                disabled={deptSelectDisabled}
                                componentsProps={{
                                  paper: {
                                    sx: {
                                      backgroundColor: darkMode
                                        ? "#1E293B"
                                        : "#fff",
                                      color: darkMode ? "#E2E8F0" : "#101828",
                                    },
                                  },
                                }}
                                sx={{
                                  width: "100%",
                                  maxWidth: 260,
                                  "& .MuiAutocomplete-tag": {
                                    display: "none",
                                  },
                                }}
                              />
                            </span>
                          </Tooltip>
                        ) : column.id === "access" ? (
                          <Select
                            fullWidth
                            value={row.access}
                            onChange={(event) =>
                              handleAccessChange(event, index)
                            }
                            size="small"
                            disabled={roleSelectDisabled}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: darkMode
                                    ? "#1E293B"
                                    : "#fff",
                                  color: darkMode ? "#E2E8F0" : "#101828",
                                },
                              },
                            }}
                            sx={{
                              borderRadius: "10px",
                              color: darkMode ? "#E2E8F0" : "#101828",
                              backgroundColor: roleSelectDisabled
                                ? darkMode
                                  ? "#1E293B"
                                  : "#F9FAFB"
                                : darkMode
                                  ? "#1E293B"
                                  : "white",
                              "& .MuiSelect-select": { py: 1 },
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: darkMode ? "#334155" : undefined,
                              },
                              "& .MuiSvgIcon-root": {
                                color: darkMode ? "#94A3B8" : undefined,
                              },
                            }}
                          >
                            {roles.map((role) => (
                              <MenuItem key={role.role} value={role.role}>
                                {role.role}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : column.id === "delete" ? (
                          <IconButton
                            onClick={() =>
                              !isSelf && canManage && requestDeleteUser(index)
                            }
                            disabled={isSelf || !canManage}
                            aria-label="delete user"
                            sx={{
                              p: 1,
                              borderRadius: "8px",
                              backgroundColor:
                                isSelf || !canManage
                                  ? darkMode
                                    ? "#1E293B"
                                    : "#F9FAFB"
                                  : "#FEF3F2",
                              "&:hover": {
                                backgroundColor:
                                  isSelf || !canManage
                                    ? darkMode
                                      ? "#1E293B"
                                      : "#F9FAFB"
                                    : "#FEE4E2",
                              },
                            }}
                          >
                            <img
                              src={Trash}
                              alt="Delete"
                              style={{
                                width: 20,
                                height: 20,
                                filter:
                                  isSelf || !canManage
                                    ? "grayscale(1) opacity(0.3)"
                                    : "brightness(0) saturate(100%) invert(18%) sepia(96%) saturate(7495%) hue-rotate(356deg) brightness(92%) contrast(118%)",
                              }}
                            />
                          </IconButton>
                        ) : (
                          row[column.id]
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MUI Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={closeConfirm} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold" }}>Delete User</DialogTitle>

        <DialogContent>
          <DialogContentText sx={{ mt: 1 }}>
            Are you sure you want to delete{" "}
            <strong>
              {pendingUser?.name} {pendingUser?.last_name}
            </strong>
            ?
            <br />
            <br />
            This action is permanent — you can’t restore the data after.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ pb: 2, pr: 3 }}>
          <Button onClick={closeConfirm} variant="outlined" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteUser}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
